import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { PricePinItem, VerdictValueItem } from './fair-price-verdict.types';
import { Card } from '../../card/card';
import { ValueAmount } from '../../value/amount/value-amount';
import { ValuePercents } from '../../value/percents/value-precents';
import { Badge } from '../../badge/badge';
import { Stack } from '../../stack/stack';
import { Text } from '../../text/text';
import { IconArrowUp } from '../../icons/arrow-up';
import { IconArrowDown } from '../../icons/arrow-down';

interface FairPriceVerdictProps {
    values: VerdictValueItem[];
    pins: PricePinItem[];
    upsideDownside: number;
    currency?: string;
    priceDiff?: number;
    badgeSuffix?: string;
    showBadge?: boolean;
    children?: React.ReactNode;
}

const getPinPosition = (pins: PricePinItem[], pin: PricePinItem): number => {
    const allValues = pins.map((p) => p.value);
    const min = Math.min(...allValues);
    const max = Math.max(...allValues);

    if (max === min) return 50;

    const range = max - min;
    const padding = range * 0.15;
    const normalizedMin = min - padding;
    const normalizedMax = max + padding;

    return 10 + ((pin.value - normalizedMin) / (normalizedMax - normalizedMin)) * 80;
};

const getResolvedLabelCenters = (raw: number[], sizes: number[], containerSize: number, gapPx: number): number[] => {
    const centers = raw.map((center, index) => {
        const halfSize = sizes[index] / 2;
        return Math.min(Math.max(center, halfSize), containerSize - halfSize);
    });

    for (let i = 1; i < centers.length; i += 1) {
        const minDistance = sizes[i - 1] / 2 + sizes[i] / 2 + gapPx;
        centers[i] = Math.max(centers[i], centers[i - 1] + minDistance);
    }

    const overflowRight = centers[centers.length - 1] + sizes[centers.length - 1] / 2 - containerSize;
    if (overflowRight > 0) {
        for (let i = 0; i < centers.length; i += 1) {
            centers[i] -= overflowRight;
        }
    }

    for (let i = centers.length - 2; i >= 0; i -= 1) {
        const minDistance = sizes[i] / 2 + sizes[i + 1] / 2 + gapPx;
        centers[i] = Math.min(centers[i], centers[i + 1] - minDistance);
    }

    const overflowLeft = sizes[0] / 2 - centers[0];
    if (overflowLeft > 0) {
        for (let i = 0; i < centers.length; i += 1) {
            centers[i] += overflowLeft;
        }
    }

    return centers;
};

export const FairPriceVerdict: React.FC<FairPriceVerdictProps> = observer(
    ({ values, pins, upsideDownside, currency = '$', priceDiff, badgeSuffix, showBadge = true, children }) => {
        const { t } = useTranslation();
        const theme = useTheme();
        const isPositive = upsideDownside > 0;
        const accentColor = isPositive ? theme.palette.color.positive.main : theme.palette.color.negative.main;
        const barRef = useRef<HTMLDivElement | null>(null);
        const labelRefs = useRef<Record<PricePinItem['variant'], HTMLSpanElement | null>>({
            current: null,
            fair: null,
            mos: null,
        });

        const sortedPins = useMemo(
            () => [...pins].sort((a, b) => getPinPosition(pins, a) - getPinPosition(pins, b)),
            [pins],
        );

        const rawLabelLeftPositions = useMemo(
            () =>
                pins.reduce(
                    (acc, pin) => ({ ...acc, [pin.variant]: getPinPosition(pins, pin) }),
                    {} as Record<PricePinItem['variant'], number>,
                ),
            [pins],
        );

        const rawLabelTopPositions = useMemo(
            () =>
                pins.reduce(
                    (acc, pin) => ({ ...acc, [pin.variant]: 100 - getPinPosition(pins, pin) }),
                    {} as Record<PricePinItem['variant'], number>,
                ),
            [pins],
        );

        const [labelLeftPositions, setLabelLeftPositions] =
            useState<Record<PricePinItem['variant'], number>>(rawLabelLeftPositions);
        const [labelTopPositions, setLabelTopPositions] =
            useState<Record<PricePinItem['variant'], number>>(rawLabelTopPositions);

        useLayoutEffect(() => {
            if (!barRef.current) {
                setLabelLeftPositions(rawLabelLeftPositions);
                setLabelTopPositions(rawLabelTopPositions);
                return;
            }

            const barWidth = barRef.current.offsetWidth;
            const barHeight = barRef.current.offsetHeight;

            if (!barWidth || !barHeight) {
                setLabelLeftPositions(rawLabelLeftPositions);
                setLabelTopPositions(rawLabelTopPositions);
                return;
            }

            const isMobile = barHeight > 100;

            if (isMobile) {
                const sortedByTop = [...pins].sort(
                    (a, b) => rawLabelTopPositions[a.variant] - rawLabelTopPositions[b.variant],
                );

                const rawCenters = sortedByTop.map((pin) => (rawLabelTopPositions[pin.variant] / 100) * barHeight);
                const labelHeights = sortedByTop.map((pin) => labelRefs.current[pin.variant]?.offsetHeight ?? 0);
                const resolved = getResolvedLabelCenters(rawCenters, labelHeights, barHeight, 6);

                const nextTop = sortedByTop.reduce(
                    (acc, pin, index) => ({
                        ...acc,
                        [pin.variant]: (resolved[index] / barHeight) * 100,
                    }),
                    {} as Record<PricePinItem['variant'], number>,
                );

                setLabelLeftPositions(rawLabelLeftPositions);
                setLabelTopPositions(nextTop);
                return;
            }

            const rawCenters = sortedPins.map((pin) => (rawLabelLeftPositions[pin.variant] / 100) * barWidth);
            const labelWidths = sortedPins.map((pin) => labelRefs.current[pin.variant]?.offsetWidth ?? 0);
            const resolved = getResolvedLabelCenters(rawCenters, labelWidths, barWidth, 10);

            const nextLeft = sortedPins.reduce(
                (acc, pin, index) => ({
                    ...acc,
                    [pin.variant]: (resolved[index] / barWidth) * 100,
                }),
                {} as Record<PricePinItem['variant'], number>,
            );

            setLabelLeftPositions(nextLeft);
            setLabelTopPositions(rawLabelTopPositions);
        }, [pins, rawLabelLeftPositions, rawLabelTopPositions, sortedPins]);

        const getPinColor = (pin: PricePinItem) => {
            if (pin.variant === 'fair') {
                return accentColor;
            }

            if (pin.variant === 'mos') {
                return theme.palette.common.textMuted;
            }

            return theme.palette.common.text;
        };

        const getValueColor = (item: VerdictValueItem) => {
            if (item.positive) {
                return theme.palette.color.positive.main;
            }

            if (item.negative) {
                return theme.palette.color.negative.main;
            }

            return theme.palette.common.textMuted;
        };

        return (
            <Card padding="none" marginTop="vsmall" shadow="large" radius="large">
                <div className="relative z-[1]">
                    <div className="px-small pb-small pt-medium">
                        <div className="mb-medium text-[1.8rem] font-bold leading-[2.2rem] text-brand-secondary">
                            {t('layout.dialogs.fairPrice.result.title')}
                        </div>

                        <div
                            ref={barRef}
                            className="relative my-[1.8rem] mb-[2.2rem] h-[5.2rem] sm:max:my-[1.2rem] sm:max:mb-[1.6rem] sm:max:h-[16rem]"
                        >
                            <div
                                className="absolute left-0 right-0 top-[2.8rem] h-[1rem] rounded-[1rem] sm:max:bottom-0 sm:max:left-[0.6rem] sm:max:top-0 sm:max:h-auto sm:max:w-[0.8rem] sm:max:rounded-[0.8rem]"
                                style={{
                                    background: `linear-gradient(90deg, ${theme.palette.color.negative.main}40 0%, ${theme.palette.color.warning.main}33 25%, ${theme.palette.color.positive.main}33 50%, ${theme.palette.color.positive.main}50 100%)`,
                                }}
                            >
                                <div
                                    className="absolute inset-x-0 inset-y-[-0.3rem] rounded-[0.8rem] blur-[3px] sm:max:inset-x-[-0.3rem] sm:max:inset-y-0"
                                    style={{
                                        background: `linear-gradient(90deg, ${theme.palette.color.negative.main}08 0%, ${theme.palette.color.warning.main}06 25%, ${theme.palette.color.positive.main}06 50%, ${theme.palette.color.positive.main}08 100%)`,
                                    }}
                                />
                            </div>

                            {pins.map((pin) => {
                                const pinPosition = getPinPosition(pins, pin);
                                const pinColor = getPinColor(pin);
                                const labelLeft = labelLeftPositions[pin.variant] ?? pinPosition;
                                const labelTop = labelTopPositions[pin.variant] ?? 100 - pinPosition;

                                return (
                                    <React.Fragment key={pin.variant}>
                                        <span
                                            data-fair-price-pin-label={pin.variant}
                                            ref={(element) => {
                                                labelRefs.current[pin.variant] = element;
                                            }}
                                            className="absolute top-0 z-[3] mb-[0.6rem] whitespace-nowrap text-[1.2rem] font-bold uppercase sm:max:left-[2.6rem] sm:max:mb-0"
                                            style={{
                                                left: `${labelLeft}%`,
                                                transform: 'translateX(-50%)',
                                                color: pinColor,
                                            }}
                                        >
                                            {pin.label}{' '}
                                            <ValueAmount
                                                mode="pure"
                                                amount={pin.value}
                                                currency={currency}
                                                fractionDigits={0}
                                            />
                                        </span>
                                        <div
                                            data-fair-price-pin-dot={pin.variant}
                                            className="absolute top-[2.4rem] z-[2] h-[1.8rem] w-[1.8rem] rounded-full border-2 sm:max:left-[1rem]"
                                            style={{
                                                left: `${pinPosition}%`,
                                                transform: 'translateX(-50%)',
                                                background: pinColor,
                                                borderColor: theme.palette.background.body,
                                                boxShadow: pin.variant === 'mos' ? undefined : `0 0 1rem ${pinColor}66`,
                                            }}
                                        />
                                        <style>{`
                                            @media ${theme.breakpoints.sm.max} {
                                                [data-fair-price-pin-label='${pin.variant}'] {
                                                    top: ${labelTop}%;
                                                    left: 2.6rem;
                                                    transform: translateY(-50%);
                                                }
                                                [data-fair-price-pin-dot='${pin.variant}'] {
                                                    top: ${100 - pinPosition}%;
                                                    left: 1rem;
                                                    transform: translate(-50%, -50%);
                                                }
                                            }
                                        `}</style>
                                    </React.Fragment>
                                );
                            })}
                        </div>

                        <div className="mb-small grid grid-cols-1 gap-vsmall sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.45fr)]">
                            {values.map((item) => (
                                <div key={item.label} className="flex flex-col">
                                    <div
                                        className="text-[2rem] font-black leading-[2rem]"
                                        style={{ color: getValueColor(item) }}
                                    >
                                        {item.format === 'percent' ? (
                                            <Stack direction="horizontal" alignItems="center" gap="xxsmall">
                                                {item.value > 0 ? <IconArrowUp /> : <IconArrowDown />}
                                                <ValuePercents mode="pure" amount={item.value} fractionDigits={2} />
                                            </Stack>
                                        ) : (
                                            <ValueAmount
                                                mode="pure"
                                                amount={item.value}
                                                currency={currency}
                                                fractionDigits={0}
                                            />
                                        )}
                                    </div>
                                    <div className="mt-xsmall text-vsmall font-bold uppercase leading-vsmall text-text-muted opacity-60">
                                        {item.label}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {showBadge && (
                            <Stack direction="horizontal" alignItems="center" justifyContent="flex-start" gap="xsmall">
                                <Badge variant={isPositive ? 'positive' : 'negative'} size="large">
                                    <span className="mr-[0.4rem] inline-flex items-center justify-center [&_i]:h-[0.5rem] [&_i]:w-[0.8rem]">
                                        {isPositive ? <IconArrowUp /> : <IconArrowDown />}
                                    </span>
                                    {priceDiff !== undefined && (
                                        <>
                                            {' '}
                                            {isPositive ? '+' : '-'}
                                            <ValueAmount
                                                mode="pure"
                                                amount={Math.abs(priceDiff)}
                                                currency={currency}
                                                fractionDigits={0}
                                            />
                                        </>
                                    )}{' '}
                                    ({isPositive ? '+' : ''}
                                    <ValuePercents mode="pure" amount={upsideDownside} fractionDigits={2} />)
                                </Badge>
                                {badgeSuffix && (
                                    <Text tag="span" variant="secondary" size="small" semiBold>
                                        {badgeSuffix}
                                    </Text>
                                )}
                            </Stack>
                        )}

                        {children ? <div className="mt-medium">{children}</div> : null}
                    </div>
                </div>
            </Card>
        );
    },
);
